import { Navigate, Route, Routes, Outlet } from 'react-router-dom'
import { RequisitionList } from './components/RequisitionList'
import RFQManagement from './components/RFQManagement'
import { QuotationManagement } from './components/QuotationManagement'
import { ProcurementErrorBoundary } from './components/ProcurementErrorBoundary'
import CrewQuotationFeed from './components/CrewQuotationFeed'
import { PurchaseOrdersList } from './components/PurchaseOrderList'
import VendorLogisticsChangeReview from './components/VendorLogisticsChangeReview'
import DeliveryTracking from './components/DeliveryTracking'
import GoodsReceipt from './components/GoodsReceipt'
import InvoiceManagement from './components/InvoiceManagement'
import ApprovalWorkflowPanel from './components/ApprovalWorkflowPanel'
import ApprovalLimitsPanel from './components/ApprovalLimitsPanel'
import CurrencyExchangeRatesPanel from './components/CurrencyExchangeRatesPanel'
import { InventoryList } from './components/InventoryList'
import { ProcurementPermissionProvider, useProcurementPermissions } from './core/permissionAccess'
import { PROCUREMENT_PERMS } from './core/permissions'
import './components/ProcurementModalLayout.css'

const ProcurementRoutes = () => {
    const { loading, canAny } = useProcurementPermissions()

    const routeAccess = {
        requisition: canAny([PROCUREMENT_PERMS.READ_REQUISITION]),
        rfq: canAny([PROCUREMENT_PERMS.RFQ_VIEW]),
        quotation: canAny([PROCUREMENT_PERMS.QUOTATION_VIEW]),
        crewQuotationFeed: canAny([PROCUREMENT_PERMS.QUOTATION_VIEW, PROCUREMENT_PERMS.QUOTATION_SELECT]),
        purchaseOrders: canAny([PROCUREMENT_PERMS.READ_PURCHASE_ORDER]),
        vendorLogisticsRequests: canAny([
            PROCUREMENT_PERMS.PO_VENDOR_LOGISTICS_VIEW,
            PROCUREMENT_PERMS.PO_VENDOR_LOGISTICS_REVIEW,
        ]),
        deliveryTracking: canAny([PROCUREMENT_PERMS.READ_DELIVERY_TRACKING]),
        goodsReceipt: canAny([PROCUREMENT_PERMS.READ_GOODS_RECEIPT]),
        invoice: canAny([PROCUREMENT_PERMS.INVOICE_VIEW]),
        approvalWorkflow: canAny([
            PROCUREMENT_PERMS.RFQ_VIEW,
            PROCUREMENT_PERMS.READ_PURCHASE_ORDER,
            PROCUREMENT_PERMS.INVOICE_VIEW,
        ]),
        currencyExchangeRates: canAny([PROCUREMENT_PERMS.READ_CURRENCY_EXCHANGE_RATE]),
        inventory: canAny([
            PROCUREMENT_PERMS.READ_LIBRARY_CATEGORY,
            PROCUREMENT_PERMS.LIBRARY_HEAD_VIEW,
            PROCUREMENT_PERMS.LIBRARY_SUBHEAD_VIEW,
            PROCUREMENT_PERMS.LIBRARY_SUBCATEGORY_VIEW,
            PROCUREMENT_PERMS.LIBRARY_ACCOUNTING_VIEW,
            PROCUREMENT_PERMS.LIBRARY_SUBACCOUNTING_VIEW,
            PROCUREMENT_PERMS.LIBRARY_ADDRESS_VIEW,
            PROCUREMENT_PERMS.READ_TERMS_CONDITIONS_LIBRARY,
            PROCUREMENT_PERMS.READ_APPROVAL_POLICY,
            PROCUREMENT_PERMS.VENDOR_LIBRARY_VIEW,
        ]),
        approvalLibrary: canAny([PROCUREMENT_PERMS.READ_APPROVAL_LIMIT, PROCUREMENT_PERMS.READ_APPROVAL_POLICY]),
    }

    const fallbackPath =
        (routeAccess.requisition && '/procurement/requisition') ||
        (routeAccess.rfq && '/procurement/rfq-management') ||
        (routeAccess.quotation && '/procurement/quotation-management') ||
        (routeAccess.crewQuotationFeed && '/procurement/crew-quotation-feed') ||
        (routeAccess.purchaseOrders && '/procurement/purchase-orders') ||
        (routeAccess.deliveryTracking && '/procurement/delivery-tracking') ||
        (routeAccess.goodsReceipt && '/procurement/goods-receipt') ||
        (routeAccess.invoice && '/procurement/invoice-management') ||
        (routeAccess.approvalLibrary && '/procurement/approval-library') ||
        (routeAccess.approvalWorkflow && '/procurement/approval-workflow') ||
        (routeAccess.currencyExchangeRates && '/procurement/currency-exchange-rates') ||
        (routeAccess.inventory && '/procurement/inventory-management') ||
        null

    if (loading) {
        return (
            <div className='d-flex align-items-center justify-content-center py-10'>
                <div className='spinner-border text-primary' role='status'>
                    <span className='visually-hidden'>Loading procurement permissions...</span>
                </div>
            </div>
        )
    }

    if (!fallbackPath) {
        return (
            <div className='alert alert-warning m-6'>
                You do not have access to procurement.
            </div>
        )
    }

    const deniedElement = <div className='alert alert-warning m-6'>You do not have access to this page.</div>
    const hiddenMenuRedirect = <Navigate to={fallbackPath} replace />

    return (
        <Routes>
            <Route element={<Outlet />}>
                <Route
                    index
                    element={<Navigate to={fallbackPath} replace />}
                />
                <Route
                    path='overview'
                    element={hiddenMenuRedirect}
                />
                <Route
                    path='requisition'
                    element={routeAccess.requisition ? <RequisitionList /> : deniedElement}
                />
                {/* /approvals redirects: Approvals.tsx is legacy/unmounted; use requisition list for workflows */}
                <Route
                    path='approvals'
                    element={
                        <Navigate
                            to='/procurement/requisition'
                            replace
                        />
                    }
                />
                <Route
                    path='rfq-management'
                    element={routeAccess.rfq ? <RFQManagement /> : deniedElement}
                />
                <Route
                    path='quotation-management'
                    element={
                        routeAccess.quotation ? (
                            <ProcurementErrorBoundary>
                                <QuotationManagement />
                            </ProcurementErrorBoundary>
                        ) : (
                            deniedElement
                        )
                    }
                />
                <Route
                    path='crew-quotation-feed'
                    element={routeAccess.crewQuotationFeed ? <CrewQuotationFeed /> : deniedElement}
                />
                <Route
                    path='purchase-orders'
                    element={routeAccess.purchaseOrders ? <PurchaseOrdersList /> : deniedElement}
                />
                <Route
                    path='company-po-info'
                    element={hiddenMenuRedirect}
                />
                <Route
                    path='delivery-tracking'
                    element={routeAccess.deliveryTracking ? <DeliveryTracking /> : deniedElement}
                />
                <Route
                    path='goods-receipt'
                    element={routeAccess.goodsReceipt ? <GoodsReceipt /> : deniedElement}
                />
                <Route
                    path='invoice-management'
                    element={routeAccess.invoice ? <InvoiceManagement /> : deniedElement}
                />
                <Route
                    path='approval-workflow'
                    element={routeAccess.approvalWorkflow ? <ApprovalWorkflowPanel /> : deniedElement}
                />
                <Route
                    path='currency-exchange-rates'
                    element={routeAccess.currencyExchangeRates ? <CurrencyExchangeRatesPanel /> : deniedElement}
                />
                <Route
                    path='vendor-logistics-change-requests'
                    element={
                        routeAccess.vendorLogisticsRequests ? (
                            <VendorLogisticsChangeReview />
                        ) : (
                            deniedElement
                        )
                    }
                />
                <Route
                    path='audit-trail'
                    element={hiddenMenuRedirect}
                />
                <Route
                    path='inventory-management'
                    element={routeAccess.inventory ? <InventoryList /> : deniedElement}
                />
                <Route
                    path='approval-library'
                    element={routeAccess.approvalLibrary ? <ApprovalLimitsPanel /> : deniedElement}
                />
                <Route
                    path='user-management'
                    element={hiddenMenuRedirect}
                />
            </Route>
        </Routes>
    )
}

const ProcurementPage = () => (
    <ProcurementPermissionProvider>
        <ProcurementRoutes />
    </ProcurementPermissionProvider>
)

export default ProcurementPage
